import { useCallback, useEffect, useRef, useState } from "react";
import { Alert, Button, Form, Input, Modal, ModalHeader } from "reactstrap";
import ModalBody from "reactstrap/lib/ModalBody";
import ModalFooter from "reactstrap/lib/ModalFooter";
import { QRCodeSVG } from "qrcode.react";
import ButtonLoader from "components/ButtonLoader";
import { Loading } from "components/Loading";
import {
  activateMfa,
  disableMfa,
  emitMfaStatusChanged,
  getMfaStatus,
  MfaStatus,
  startMfaEnrollment,
} from "store/persistence/persistMfa";

/**
 * The "Two-factor authentication" row on Your Profile. Enabling walks a
 * two-step wizard: scan the QR (or enter the secret manually) and prove one
 * code; then the ten one-time recovery codes are shown - the only time they
 * ever are. Disabling asks for a current code, so an unlocked laptop is not
 * enough to strip the protection.
 */
const TwoFactorSettings = ({
  onStatusChange,
}: {
  /** Fires with the fresh enabled flag every time status is (re)fetched. */
  onStatusChange?: (enabled: boolean) => void;
}) => {
  const [status, setStatus] = useState<MfaStatus | null>(null);
  const [error, setError] = useState<string | undefined>();
  const [busy, setBusy] = useState(false);

  // wizard state
  const [wizard, setWizard] = useState<
    | null
    | {
        step: "scan";
        /** absent when an existing authenticator entry is being reused */
        secret?: string;
        otpauthUrl?: string;
        code: string;
        reusedExistingAuthenticator?: boolean;
      }
    | { step: "codes"; recoveryCodes: string[]; copied: boolean }
  >(null);
  const [disableDialog, setDisableDialog] = useState<{ code: string } | null>(
    null
  );
  // The enforcement takeover unmounts this component the moment it hears
  // "enabled" - which must not happen while the recovery codes are still
  // on screen. Track the wizard in a ref so refresh() can hold the signal.
  const wizardRef = useRef<typeof wizard>(null);
  wizardRef.current = wizard;

  const refresh = useCallback(async () => {
    try {
      const fresh = await getMfaStatus();
      setStatus(fresh);
      if (!wizardRef.current) {
        onStatusChange?.(fresh.enabled);
        // ...and tell the enforcement banner, which lives elsewhere in the
        // tree and would otherwise keep showing until a full page load
        emitMfaStatusChanged(fresh.enabled);
      }
    } catch (e) {
      setError((e as Error).message);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => {
    refresh();
  }, [refresh]);

  const startWizard = async (opts: { regenerate?: boolean } = {}) => {
    setBusy(true);
    setError(undefined);
    try {
      const {
        secret,
        otpauthUrl,
        reusedExistingAuthenticator,
      } = await startMfaEnrollment(opts);
      setWizard({
        step: "scan",
        secret,
        otpauthUrl,
        code: "",
        reusedExistingAuthenticator,
      });
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const activate = async () => {
    if (wizard?.step !== "scan" || !wizard.code) {
      return;
    }
    setBusy(true);
    setError(undefined);
    try {
      const { recoveryCodes } = await activateMfa(wizard.code);
      setWizard({ step: "codes", recoveryCodes, copied: false });
      refresh();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const doDisable = async () => {
    if (!disableDialog?.code) {
      return;
    }
    setBusy(true);
    setError(undefined);
    try {
      await disableMfa(disableDialog.code);
      setDisableDialog(null);
      refresh();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const copyRecoveryCodes = () => {
    if (wizard?.step !== "codes") {
      return;
    }
    navigator.clipboard?.writeText(wizard.recoveryCodes.join("\n"));
    setWizard({ ...wizard, copied: true });
  };

  if (!status) {
    return <Loading size="small" />;
  }

  return (
    <>
      {status.enabled ? (
        <span data-testid="mfa-status-enabled">
          <span className="text-success font-weight-medium">Enabled</span>
          <span className="text-muted">
            {" "}
            · {status.recoveryCodesLeft} recovery{" "}
            {status.recoveryCodesLeft === 1 ? "code" : "codes"} left
          </span>
          <Button
            color="custom-outline-dark"
            size="sm"
            className="ml-3"
            data-testid="mfa-disable-button"
            onClick={() => {
              setError(undefined);
              setDisableDialog({ code: "" });
            }}
          >
            Turn off
          </Button>
        </span>
      ) : (
        <span data-testid="mfa-status-disabled">
          <span className="text-muted">
            Protect your account with an authenticator app.
          </span>
          <ButtonLoader
            color="primary"
            size="sm"
            className="ml-3"
            loading={busy && !wizard}
            data-testid="mfa-enable-button"
            onClick={() => startWizard()}
          >
            Enable
          </ButtonLoader>
        </span>
      )}
      {error && !wizard && !disableDialog && (
        <Alert color="warning" className="mt-2 mb-0">
          {error}
        </Alert>
      )}

      {/* ---- enrolment wizard ---- */}
      {wizard?.step === "scan" && (
        <Modal
          isOpen={true}
          toggle={() => setWizard(null)}
          autoFocus={false}
          centered={true}
        >
          <Form
            onSubmit={(e) => {
              e.preventDefault();
              activate();
            }}
          >
            <ModalHeader>Set up two-factor authentication</ModalHeader>
            <ModalBody>
              {wizard.reusedExistingAuthenticator ? (
                // Two-factor is set up per account, but the person keeps ONE
                // authenticator entry: the server reuses the secret they
                // already proved elsewhere and never sends it back - they
                // prove possession with a code from the entry they have.
                <>
                  <p data-testid="mfa-reused-hint">
                    You already use an authenticator entry for Encharge, and
                    this account shares it - there is nothing new to scan. Enter
                    the current code from that entry.
                  </p>
                  <p className="text-muted small">
                    Lost that authenticator?{" "}
                    <Button
                      color="link"
                      size="sm"
                      className="p-0 align-baseline"
                      disabled={busy}
                      data-testid="mfa-regenerate-button"
                      onClick={() => startWizard({ regenerate: true })}
                    >
                      Set up a new key instead
                    </Button>
                  </p>
                </>
              ) : (
                <>
                  <p>
                    Scan this QR code with your authenticator app (Google
                    Authenticator, 1Password, Authy…)
                  </p>
                  <div
                    className="text-center my-3 p-3 d-inline-block rounded"
                    // hardcoded: scanners need dark-on-light, and the dark
                    // theme repaints .bg-white which inverts the QR code
                    style={{ background: "#ffffff" }}
                  >
                    <QRCodeSVG
                      value={wizard.otpauthUrl || ""}
                      size={180}
                      fgColor="#000000"
                      bgColor="#ffffff"
                    />
                  </div>
                  <p className="text-muted small">
                    Can't scan? Enter this key manually:{" "}
                    <code data-testid="mfa-manual-secret">{wizard.secret}</code>
                  </p>
                </>
              )}
              <p className="mb-1 mt-3">
                {wizard.reusedExistingAuthenticator
                  ? "Enter the 6-digit code your app shows:"
                  : "Then enter the 6-digit code the app shows:"}
              </p>
              <Input
                autoFocus={true}
                inputMode="numeric"
                autoComplete="one-time-code"
                placeholder="123456"
                value={wizard.code}
                data-testid="mfa-activate-code"
                onChange={(e) => setWizard({ ...wizard, code: e.target.value })}
              />
              {error && (
                <Alert color="warning" className="mt-3 mb-0">
                  {error}
                </Alert>
              )}
            </ModalBody>
            <ModalFooter>
              <Button color="link" onClick={() => setWizard(null)}>
                Cancel
              </Button>{" "}
              <ButtonLoader
                color="primary"
                loading={busy}
                data-testid="mfa-activate-button"
                onClick={activate}
              >
                Verify &amp; enable
              </ButtonLoader>
            </ModalFooter>
          </Form>
        </Modal>
      )}
      {wizard?.step === "codes" && (
        <Modal
          isOpen={true}
          toggle={() => setWizard(null)}
          centered={true}
          // shown exactly once: clicking the backdrop or pressing Escape must
          // not be able to throw them away. "I saved them" is the only way out.
          backdrop="static"
          keyboard={false}
        >
          <ModalHeader>Save your recovery codes</ModalHeader>
          <ModalBody>
            <p>
              If you lose your phone, one of these codes signs you in. Each
              works once. <strong>This is the only time they are shown.</strong>
            </p>
            <pre
              className="p-3 rounded mfa-recovery-codes"
              data-testid="mfa-recovery-codes"
              style={{ columnCount: 2 }}
            >
              {wizard.recoveryCodes.join("\n")}
            </pre>
            {wizard.copied && (
              <Alert color="success" className="mb-0">
                Copied to clipboard.
              </Alert>
            )}
          </ModalBody>
          <ModalFooter>
            <Button color="custom-outline-dark" onClick={copyRecoveryCodes}>
              Copy codes
            </Button>{" "}
            <Button
              color="primary"
              data-testid="mfa-codes-done"
              onClick={() => {
                wizardRef.current = null;
                setWizard(null);
                refresh();
              }}
            >
              I saved them
            </Button>
          </ModalFooter>
        </Modal>
      )}

      {/* ---- disable dialog ---- */}
      {disableDialog && (
        <Modal
          isOpen={true}
          toggle={() => setDisableDialog(null)}
          autoFocus={false}
          centered={true}
        >
          <Form
            onSubmit={(e) => {
              e.preventDefault();
              doDisable();
            }}
          >
            <ModalHeader>Turn off two-factor authentication</ModalHeader>
            <ModalBody>
              <p>
                Enter a current authenticator code (or one of your recovery
                codes) to confirm.
              </p>
              <Input
                autoFocus={true}
                autoComplete="one-time-code"
                placeholder="123456 or XXXX-XXXX-XXXX-XXXX"
                value={disableDialog.code}
                data-testid="mfa-disable-code"
                onChange={(e) => setDisableDialog({ code: e.target.value })}
              />
              {error && (
                <Alert color="warning" className="mt-3 mb-0">
                  {error}
                </Alert>
              )}
            </ModalBody>
            <ModalFooter>
              <Button color="link" onClick={() => setDisableDialog(null)}>
                Cancel
              </Button>{" "}
              <ButtonLoader
                color="danger"
                loading={busy}
                data-testid="mfa-disable-confirm"
                onClick={doDisable}
              >
                Turn off
              </ButtonLoader>
            </ModalFooter>
          </Form>
        </Modal>
      )}
    </>
  );
};

export default TwoFactorSettings;
